import MobileDrawer from './MobileDrawer';
import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaHome, FaBars, FaChevronLeft, FaChevronRight, FaChevronDown, FaSearch, FaUserMd, FaLayerGroup, FaBullseye, FaBuilding, FaUsers } from "react-icons/fa";
import { useGetCategoriesQuery } from '../slices/categoriesApiSlice';
const EMPTY_CATEGORIES = {};
const names = node => Array.isArray(node) ? node : Object.keys(node || {});

const CategoryMenu = ({ userName = "" }) => {
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useGetCategoriesQuery('available', { refetchOnMountOrArgChange: true, refetchOnFocus: true, pollingInterval: 60000, skipPollingIfUnfocused: true });
  const categoriesDB = data?.categories || EMPTY_CATEGORIES;
  const firstLevelCategories = useMemo(() => Object.keys(categoriesDB), [categoriesDB]);

  const [activeDesktopRoot, setActiveDesktopRoot] = useState(null);
  const [activeDesktopSecond, setActiveDesktopSecond] = useState(null);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileLevel, setMobileLevel] = useState(1);
  const [mobileRoot, setMobileRoot] = useState("");
  const [mobileSecond, setMobileSecond] = useState("");
  const [mobileThird, setMobileThird] = useState("");

  const resultCount = (term) => {
    const count = data?.counts?.[term];
    return Number.isFinite(count) ? <p className="m-0 text-sm font-semibold text-sky-800" role="status">{count} {count === 1 ? 'resource' : 'resources'} found for {term}</p> : null;
  };

  const goToSearch = (term) => {
    navigate(`/search/${encodeURIComponent(term)}`);
    setMobileOpen(false);
    setMobileLevel(1);
    setMobileRoot("");
    setMobileSecond("");
    setActiveDesktopRoot(null);
    setActiveDesktopSecond(null);
  };

  const handleDesktopRootEnter = (root) => {
    setActiveDesktopRoot(root);
    setActiveDesktopSecond(null);
  };

  const handleDesktopLeave = () => {
    setActiveDesktopRoot(null);
    setActiveDesktopSecond(null);
  };

  const openMobileRoot = (root) => {
    setMobileRoot(root);
    setMobileLevel(2);
  };

  const openMobileSecond = (second) => {
    setMobileSecond(second);
    setMobileLevel(3);
  };

  const mobileBack = () => {
    if (mobileLevel === 4) { setMobileLevel(3); setMobileThird(""); return; }
    if (mobileLevel === 3) {
      setMobileLevel(2);
      setMobileSecond("");
      return;
    }
    if (mobileLevel === 2) {
      setMobileLevel(1);
      setMobileRoot("");
    }
  };

  return (
    <div className="category-nav-shell border-t border-slate-200 bg-slate-50/70">
      <div className="category-bar mx-auto flex max-w-[1400px] items-center gap-3 px-4 py-2 md:px-6">
        <div className="desktop-category-nav relative min-w-0 flex-1 hidden grid-cols-5 gap-2 lg:grid" onMouseLeave={handleDesktopLeave}
          onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) handleDesktopLeave(); }}
          onKeyDown={event => { if (event.key === 'Escape') { const trigger = event.currentTarget.querySelector('[aria-expanded="true"]'); trigger?.focus(); handleDesktopLeave(); } }}>
          {isLoading && <span role="status" className="p-2 text-sm text-slate-500">Loading categories...</span>}
          {error && <button type="button" onClick={refetch} className="p-2 text-sm text-sky-700">Could not load categories. Retry</button>}
          {firstLevelCategories.map((root, rootIndex) => {
            const Icon = [FaUserMd, FaLayerGroup, FaBullseye, FaBuilding, FaUsers][rootIndex % 5];
            const secondLevel = Object.keys(categoriesDB[root] || {});
            const currentSecond = activeDesktopSecond || secondLevel[0];
            const thirdNode = categoriesDB[root]?.[currentSecond] || [];
            const thirdLevel = names(thirdNode);

            return (
              <div key={root} className="min-w-0">
                <button
                  type="button"
                  onMouseEnter={() => handleDesktopRootEnter(root)}
                  onClick={() => activeDesktopRoot === root ? handleDesktopLeave() : handleDesktopRootEnter(root)}
                  aria-expanded={activeDesktopRoot === root}
                  aria-controls={`desktop-categories-${rootIndex}`}
                  className={`desktop-category-trigger ${activeDesktopRoot === root ? 'is-active' : ''}`}
                >
                  <span className="desktop-category-icon"><Icon aria-hidden="true" /></span>
                  <span className="min-w-0 flex-1">{root}</span>
                  <FaChevronDown aria-hidden="true" className={`shrink-0 text-[10px] transition-transform ${activeDesktopRoot === root ? 'rotate-180' : ''}`} />
                </button>

                {activeDesktopRoot === root && (
                  <div id={`desktop-categories-${rootIndex}`} className="desktop-category-panel" role="region" aria-label={root}>
                    <div className="desktop-category-intro">
                      <h3 className="m-0 text-sm font-semibold text-slate-900">{root}</h3>
                    </div>
                    <div className="desktop-category-body">
                    <div className="desktop-category-groups space-y-1 overflow-y-auto pr-1">
                      {secondLevel.map((second) => (
                        <button
                          key={second}
                          type="button"
                          onMouseEnter={() => setActiveDesktopSecond(second)}
                          onFocus={() => setActiveDesktopSecond(second)}
                          onClick={() => goToSearch(second)}
                          className={`desktop-category-group flex w-full items-center justify-between gap-2 rounded-lg border text-left font-medium transition ${
                            currentSecond === second
                              ? "border-sky-200 bg-sky-50 text-sky-800"
                              : "border-slate-200 bg-white text-slate-700 hover:bg-sky-50"
                          }`}
                        >
                          <span>{second}</span>
                          <FaChevronRight className="text-xs opacity-60" />
                        </button>
                      ))}
                    </div>

                    <div className="desktop-category-options min-w-0 overflow-y-auto rounded-xl border border-slate-200 bg-white p-2">
                      <button type="button" onClick={() => goToSearch(currentSecond)} className="desktop-category-view-all mb-2 flex items-center gap-2 rounded-lg bg-sky-600 font-semibold text-white transition hover:bg-sky-700"><FaSearch aria-hidden="true" />View all {currentSecond}</button>
                      <div className="grid grid-cols-2 items-start gap-2 xl:grid-cols-3">
                      {thirdLevel.length > 0 ? (
                        thirdLevel.map((third) => (
                          <div key={third} className="min-w-0 rounded-lg border border-slate-100 bg-slate-50/70 p-1">
                          <button
                            type="button"
                            onClick={() => goToSearch(third)}
                            className="desktop-category-heading block w-full rounded-md text-left font-semibold text-slate-800 transition hover:bg-sky-100"
                          >
                            {third}
                          </button>
                          {!Array.isArray(thirdNode) && (thirdNode[third] || []).map(child => <button key={child} type="button" onClick={() => goToSearch(child)} className="desktop-category-link block w-full rounded-md text-left text-slate-600 transition hover:bg-sky-50 hover:text-sky-800">{child}</button>)}
                          </div>
                        ))
                      ) : (
                        <p className="px-2 py-2 text-sm text-slate-500">No subcategories</p>
                      )}
                      </div>
                    </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="category-mobile-actions flex min-w-0 flex-1 items-center gap-2 lg:hidden">
          <Link to="/" aria-label="Home" className="category-home inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-xl border border-sky-200 bg-white text-sky-800 shadow-sm hover:bg-sky-50"><FaHome aria-hidden="true" /></Link>
          <button
            type="button"
            onClick={() => { setMobileLevel(1); setMobileRoot(""); setMobileSecond(""); setMobileOpen(true); }}
            aria-haspopup="dialog"
            aria-expanded={mobileOpen}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-sky-200 bg-white px-4 py-2 text-sm font-semibold text-sky-800 shadow-sm transition hover:bg-sky-50 active:scale-[0.98]"
          >
            <FaBars />
            <span>Categories</span>
          </button>
        </div>
        {userName && <span className="category-welcome" title={`Welcome! ${userName}`}>Welcome! <strong>{userName}</strong></span>}
      </div>

      {mobileOpen && (
        <MobileDrawer title="Categories" variant="categories" onClose={() => setMobileOpen(false)}>
          <div className="category-browser">
            <div className="category-intro">
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-sky-700">Practidex library</span>
              <p className="mb-1 mt-2 text-xl font-semibold leading-tight text-slate-900">Find your next resource</p>
              <p className="m-0 text-sm leading-6 text-slate-600">Explore tools for your practice, your goals, and the people you support.</p>
            </div>
            <div className="mb-4 flex min-w-0 items-center gap-3">
              {mobileLevel > 1 && <button type="button" onClick={mobileBack} aria-label="Back"
                className="category-back"><FaChevronLeft aria-hidden="true" /></button>}
              <div className="min-w-0">
                <p className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                  {mobileLevel === 1 ? 'Start exploring' : mobileLevel === 2 ? 'Categories' : mobileRoot}
                </p>
                <h3 className="m-0 text-base font-semibold text-slate-900" aria-live="polite">
                  {mobileLevel === 1 ? 'Browse categories' : mobileLevel === 2 ? mobileRoot : mobileLevel === 3 ? mobileSecond : mobileThird}
                </h3>
              </div>
            </div>
            {isLoading && <p role="status">Loading categories...</p>}
            {error && <div role="alert" className="mb-3 rounded-xl bg-white p-3 text-sm">Could not load categories. <button type="button" onClick={refetch} className="font-semibold text-sky-700">Retry</button></div>}
            {!isLoading && !error && firstLevelCategories.length === 0 && <p role="status">No categories with available resources yet.</p>}
            <div key={`${mobileLevel}-${mobileRoot}-${mobileSecond}-${mobileThird}`} className="category-level">
              {mobileLevel === 1 && firstLevelCategories.map((root, index) => {
                const Icon = [FaUserMd, FaLayerGroup, FaBullseye, FaBuilding, FaUsers][index % 5];
                return <button key={root} type="button" onClick={() => openMobileRoot(root)} className="category-row">
                  <span className="category-icon"><Icon aria-hidden="true" /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold">{root}</span>
                    <span className="mt-1 block text-xs font-normal text-slate-500">{Object.keys(categoriesDB[root] || {}).length} categories</span>
                  </span>
                  <FaChevronRight aria-hidden="true" className="shrink-0 text-xs text-sky-600" />
                </button>;
              })}
              {mobileLevel === 2 && Object.keys(categoriesDB[mobileRoot] || {}).map(second => (
                <button key={second} type="button" className="category-row"
                  onClick={() => (names(categoriesDB[mobileRoot]?.[second]).length ? openMobileSecond(second) : goToSearch(second))}>
                  <span className="min-w-0 flex-1 font-medium">{second}</span>
                  <FaChevronRight aria-hidden="true" className="shrink-0 text-xs text-sky-600" />
                </button>
              ))}
              {mobileLevel === 3 && <>
                {resultCount(mobileSecond)}
                <button type="button" className="category-view-all" onClick={() => goToSearch(mobileSecond)}>
                  <FaSearch aria-hidden="true" className="shrink-0" />
                  <span>View all {mobileSecond}</span>
                </button>
                {names(categoriesDB[mobileRoot]?.[mobileSecond]).length > 0 && <p className="m-0 text-sm text-slate-600">Or continue exploring the subcategories below.</p>}
                {names(categoriesDB[mobileRoot]?.[mobileSecond]).map(third => (
                  <button key={third} type="button" onClick={() => { if (Array.isArray(categoriesDB[mobileRoot]?.[mobileSecond])) goToSearch(third); else { setMobileThird(third); setMobileLevel(4); } }} className="category-row">
                    <span className="min-w-0 flex-1 font-medium">{third}</span>
                    <FaChevronRight aria-hidden="true" className="shrink-0 text-xs text-sky-600" />
                  </button>
                ))}
              </>}
              {mobileLevel === 4 && <>
                {resultCount(mobileThird)}
                <button type="button" className="category-view-all" onClick={() => goToSearch(mobileThird)}><FaSearch aria-hidden="true" /><span>View all {mobileThird}</span></button>
                {(categoriesDB[mobileRoot]?.[mobileSecond]?.[mobileThird] || []).length > 0 && <p className="m-0 text-sm text-slate-600">Or continue exploring the subcategories below.</p>}
                {(categoriesDB[mobileRoot]?.[mobileSecond]?.[mobileThird] || []).map(child => <button key={child} type="button" className="category-row" onClick={() => goToSearch(child)}><span className="flex-1">{child}</span><FaChevronRight aria-hidden="true" className="text-xs text-sky-600" /></button>)}
              </>}
            </div>
          </div>
        </MobileDrawer>
      )}
    </div>
  );
};

export default CategoryMenu;
